import axios from "axios";
import ENV from "constants/env";
import { getActiveCompanyId } from "utils/companyContext";

// Production builds sometimes set REACT_APP_BASE_URL without the /api suffix.
// All service paths use /companies/... under the API, so the host must end with /api.
const normalizeApiBaseUrl = (raw) => {
    if (!raw || typeof raw !== "string") return raw;
    const trimmed = raw.trim().replace(/\/+$/, "");
    if (!trimmed) return raw;
    if (trimmed.endsWith("/api")) return trimmed;
    return `${trimmed}/api`;
};

const axiosInstance = axios.create({
    baseURL: normalizeApiBaseUrl(ENV.BASE_URL),
    headers: {
        "X-API-key": ENV.API_KEY
    }
});

axiosInstance.interceptors.request.use((config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
    }
    const url = `${config.baseURL || ""}${config.url || ""}`;
    const rel = config.url || "";
    // Firm-level APIs use /firms/... and may include a companyId in the path that is NOT
    // the "current" dashboard company. CompanyAccessFilter rejects mismatched X-Company-Id.
    const isFirmApi = /\/firms(\/|$)/i.test(url) || /^\/?firms(\/|$)/i.test(rel);
    const companyId = getActiveCompanyId();

    // Never hit /companies/null/... — happens when pages mount before a company is selected.
    if (/\/companies\/(null|undefined)(\/|$|\?)/i.test(rel) || /\/companies\/(null|undefined)(\/|$|\?)/i.test(url)) {
        return Promise.reject(new axios.Cancel("No company selected"));
    }

    if (companyId && !isFirmApi) {
        config.headers["X-Company-Id"] = companyId;
    } else if (isFirmApi && config.headers) {
        delete config.headers["X-Company-Id"];
        delete config.headers["x-company-id"];
    }
    const locationId = localStorage.getItem("locationId");
    if (locationId && !isFirmApi) {
        config.headers["X-Location-Id"] = locationId;
    }
    return config;
});

axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error?.response?.status === 401) {
            localStorage.removeItem("authToken");
            localStorage.removeItem("userRole");
            localStorage.removeItem("companyId");
            localStorage.removeItem("employeeId");
            window.location.href = "/login";
        }
        return Promise.reject(error);
    }
);

export default axiosInstance;